import React, { useState } from 'react';
import axios from 'axios';
import {
  FaBell, FaInfoCircle, FaTrash, FaChartLine, FaStar, FaChevronDown, FaUnlock,
} from 'react-icons/fa';
import { formatDateForDisplay, formatDateForInput, formSelectClass, formInputClass } from './menuTabUtils';

const DEFAULT_STEPS = [
  { step: 1, title: 'Information requirement checklist', status: 'pending' },
  { step: 2, title: 'Pending Queries', status: 'pending' },
  { step: 3, title: 'Draft financials', status: 'pending' },
  { step: 4, title: 'Final Books', status: 'pending' },
];

const BookkeepingMenuTab = ({
  selectedMenuUser,
  setSelectedMenuUser,
  handleBookkeepingServiceUpdate,
  handleRemoveBookkeepingService,
}) => {
  const [bookkeepingFrequency, setBookkeepingFrequency] = useState('');
  const [bookkeepingStartDate, setBookkeepingStartDate] = useState('');
  const [updatingBookkeepingStep, setUpdatingBookkeepingStep] = useState({});

  const handleUpdateBookkeepingStepStatus = async (stepNumber, status) => {
    setUpdatingBookkeepingStep((prev) => ({ ...prev, [stepNumber]: true }));
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${selectedMenuUser._id}/step`,
        { stepNumber, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.bookkeepingSteps) {
        setSelectedMenuUser({ ...selectedMenuUser, bookkeepingSteps: response.data.bookkeepingSteps });
      } else {
        const updatedSteps = [...(selectedMenuUser.bookkeepingSteps || DEFAULT_STEPS)];
        const idx = updatedSteps.findIndex((s) => s.step === stepNumber);
        if (idx >= 0) updatedSteps[idx] = { ...updatedSteps[idx], status };
        else updatedSteps.push({ step: stepNumber, title: DEFAULT_STEPS[stepNumber - 1]?.title, status });
        setSelectedMenuUser({ ...selectedMenuUser, bookkeepingSteps: updatedSteps });
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update bookkeeping step status.');
    } finally {
      setUpdatingBookkeepingStep((prev) => ({ ...prev, [stepNumber]: false }));
    }
  };

  const stepsList = selectedMenuUser?.bookkeepingSteps?.length > 0
    ? selectedMenuUser.bookkeepingSteps
    : DEFAULT_STEPS;

  const finalSteps = DEFAULT_STEPS.map((ds) => {
    const existing = stepsList.find((s) => s.step === ds.step);
    return existing ? existing : ds;
  });

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h6 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FaBell className="text-amber-500" /> Due Date Setup
        </h6>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Frequency</label>
          <select
            value={bookkeepingFrequency || selectedMenuUser?.bookkeepingService?.frequency || ''}
            onChange={(e) => setBookkeepingFrequency(e.target.value)}
            className={formSelectClass}
          >
            <option value="">-- Select Frequency --</option>
            <option value="Monthly">Monthly</option>
            <option value="Quarterly">Quarterly</option>
            <option value="Yearly">Yearly</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
          <input
            type="date"
            value={bookkeepingStartDate || formatDateForInput(selectedMenuUser?.bookkeepingService?.startDate)}
            onChange={(e) => setBookkeepingStartDate(e.target.value)}
            className={formInputClass}
          />
        </div>

        <div className="p-3 bg-gray-100 dark:bg-neutral-800 rounded-xl text-sm">
          <strong className="text-gray-900 dark:text-white">Due Date Rules:</strong>
          <ul className="mt-2 space-y-1 text-gray-600 dark:text-gray-400 list-disc list-inside">
            <li><strong>Monthly:</strong> Due Date is 1 month after the start date</li>
            <li><strong>Quarterly:</strong> Due Date is 4 months after the start date</li>
            <li><strong>Yearly:</strong> Due Date is 1 year after the start date</li>
          </ul>
        </div>

        <button
          type="button"
          onClick={() => {
            const freq = bookkeepingFrequency || selectedMenuUser?.bookkeepingService?.frequency;
            const start = bookkeepingStartDate || formatDateForInput(selectedMenuUser?.bookkeepingService?.startDate);
            if (freq && start) {
              handleBookkeepingServiceUpdate(selectedMenuUser._id, freq, start);
              setBookkeepingFrequency('');
              setBookkeepingStartDate('');
            }
          }}
          disabled={!(bookkeepingFrequency || selectedMenuUser?.bookkeepingService?.frequency) || !(bookkeepingStartDate || selectedMenuUser?.bookkeepingService?.startDate)}
          className="w-full px-4 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50"
        >
          Save & Calculate Due Date
        </button>

        {selectedMenuUser?.bookkeepingService?.dueDate && (
          <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-2xl border border-blue-100 dark:border-blue-800 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h6 className="font-bold text-blue-900 dark:text-blue-300 flex items-center gap-2">
                <FaInfoCircle className="text-blue-500" /> Current Bookkeeping Setup
              </h6>
              <button
                type="button"
                onClick={() => handleRemoveBookkeepingService(selectedMenuUser._id)}
                className="p-2 bg-red-100 dark:bg-red-900/20 text-red-600 rounded-lg hover:bg-red-200"
                title="Remove Service"
              >
                <FaTrash className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs bg-white dark:bg-neutral-900 p-4 rounded-xl border border-gray-100 dark:border-neutral-700">
              <div>
                <div className="text-gray-400 uppercase font-semibold text-[10px] mb-0.5">Frequency</div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">{selectedMenuUser.bookkeepingService.frequency}</div>
              </div>
              <div>
                <div className="text-gray-400 uppercase font-semibold text-[10px] mb-0.5">Start Date</div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">{formatDateForDisplay(selectedMenuUser.bookkeepingService.startDate)}</div>
              </div>
              <div className="col-span-2 pt-2 border-t border-gray-100 dark:border-neutral-700">
                <div className="text-gray-400 uppercase font-semibold text-[10px] mb-0.5">Next Due Date</div>
                <div className="text-sm font-bold text-red-600 dark:text-red-400">
                  {selectedMenuUser.bookkeepingService.dueDateText || formatDateForDisplay(selectedMenuUser.bookkeepingService.dueDate)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="pt-6 border-t border-gray-100 dark:border-neutral-700">
        <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FaChartLine className="text-primary-500" /> Bookkeeping Steps Status
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {finalSteps.map((step) => (
            <div
              key={step.step}
              className={`p-4 rounded-xl border-2 ${
                step.status === 'completed'
                  ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                  : step.status === 'submitted'
                    ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                    : 'bg-gray-50/50 dark:bg-neutral-900/50 border-gray-200 dark:border-neutral-700'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase">Step {step.step}</span>
                <span className={`px-2 py-0.5 text-[9px] font-black rounded-full uppercase ${
                  step.status === 'completed' ? 'bg-green-500 text-white'
                    : step.status === 'submitted' ? 'bg-blue-500 text-white'
                      : 'bg-gray-500 text-white'
                }`}>
                  {step.status || 'pending'}
                </span>
              </div>
              <h6 className="font-bold text-gray-900 dark:text-white text-xs mb-3">{step.title}</h6>
              <div className="relative">
                <select
                  value={step.status || 'pending'}
                  onChange={(e) => handleUpdateBookkeepingStepStatus(step.step, e.target.value)}
                  disabled={updatingBookkeepingStep[step.step]}
                  className="w-full pl-3 pr-8 py-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl text-xs font-semibold appearance-none"
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="client-pending">Client Action Pending</option>
                  <option value="submitted">Submitted</option>
                  <option value="completed">Completed</option>
                </select>
                <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-500 pointer-events-none" />
              </div>
              {step.step === 1 && (step.status === 'submitted' || step.status === 'completed') && (
                <button
                  type="button"
                  onClick={() => handleUpdateBookkeepingStepStatus(1, 'in-progress')}
                  className="w-full mt-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-bold rounded-lg flex items-center justify-center gap-1.5"
                >
                  <FaUnlock className="w-3 h-3" /> Re-open for User
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pt-6 border-t border-gray-100 dark:border-neutral-700">
        <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FaStar className="text-yellow-400" /> Bookkeeping Feedback
        </h5>
        {selectedMenuUser?.bookkeepingFeedback?.rating ? (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar key={star} size={12} className={star <= selectedMenuUser.bookkeepingFeedback.rating ? 'text-yellow-400' : 'text-gray-200'} />
                ))}
              </div>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {selectedMenuUser.bookkeepingFeedback.rating}/5
              </span>
            </div>
            {selectedMenuUser.bookkeepingFeedback.comment && (
              <p className="text-xs text-gray-700 dark:text-gray-300 italic bg-white/50 dark:bg-neutral-900/50 p-3 rounded-lg">
                &ldquo;{selectedMenuUser.bookkeepingFeedback.comment}&rdquo;
              </p>
            )}
          </div>
        ) : (
          <div className="p-4 bg-gray-50 dark:bg-neutral-900/30 border border-gray-100 dark:border-neutral-700 rounded-xl text-center">
            <p className="text-xs text-gray-400">No feedback provided yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookkeepingMenuTab;
